import {
  ISpecificationCategoryResponse,
  ISpecificationItemResponse,
  ISpecificationItemResponseExtended,
  ISpecificationCategoryOrItemResponse,
} from "@/shared/services/interface/responses/ISpecificationResponse";
import {
  ISupabaseSpecificationCategory,
  ISupabaseSpecificationItem,
} from "@/shared/services/interface/responses/ISupabaseSpecification";
import { store } from "../store";

// Helper to get subcategories from children array
export const getSubCategories = (
  category: ISpecificationCategoryResponse
): ISpecificationCategoryResponse[] => {
  if (!category.children || !Array.isArray(category.children)) {
    return [];
  }
  return category.children.filter(
    (child) => child.type === "category"
  ) as ISpecificationCategoryResponse[];
};

// Helper to get items from children array
export const getItems = (
  category: ISpecificationCategoryResponse
): ISpecificationItemResponse[] => {
  if (!category.children || !Array.isArray(category.children)) {
    return [];
  }
  return category.children.filter(
    (child) => child.type === "item"
  ) as ISpecificationItemResponse[];
};

// Helper to update children array with new subcategory
// Preserves the original order of children array
const addSubCategoryToChildren = (
  category: ISpecificationCategoryResponse,
  newSubCategory: ISpecificationCategoryResponse
): ISpecificationCategoryOrItemResponse[] => {
  const currentChildren = category.children || [];

  // Check if subcategory already exists in children array
  const existingIndex = currentChildren.findIndex(
    (child) => child.type === "category" && child.id === newSubCategory.id
  );

  if (existingIndex !== -1) {
    // Update existing subcategory, preserve its children and position
    const existingSub = currentChildren[existingIndex] as ISpecificationCategoryResponse;
    const updatedChildren = [...currentChildren];
    updatedChildren[existingIndex] = {
      ...newSubCategory,
      children: existingSub.children, // Preserve children
    };
    return updatedChildren;
  } else {
    // Add new subcategory at the end, preserving original order
    return [...currentChildren, newSubCategory];
  }
};

// Helper to update children array with new item
// Preserves the original order of children array
const addItemToChildren = (
  category: ISpecificationCategoryResponse,
  newItem: ISpecificationItemResponse
): ISpecificationCategoryOrItemResponse[] => {
  const currentChildren = category.children || [];

  // Check if item already exists in children array
  const existingIndex = currentChildren.findIndex(
    (child) => child.type === "item" && child.id === newItem.id
  );

  if (existingIndex !== -1) {
    // Update existing item, preserve its position
    const updatedChildren = [...currentChildren];
    updatedChildren[existingIndex] = newItem;
    return updatedChildren;
  } else {
    // Add new item at the end, preserving original order
    return [...currentChildren, newItem];
  }
};

// Helper to remove item from children array
// Preserves the original order of children array
const removeItemFromChildren = (
  category: ISpecificationCategoryResponse,
  itemId: string
): ISpecificationCategoryOrItemResponse[] => {
  const currentChildren = category.children || [];
  // Filter out the item while preserving order
  return currentChildren.filter(
    (child) => !(child.type === "item" && child.id === itemId)
  );
};

export const updateCategoryRecursively = (
  categories: ISpecificationCategoryResponse[],
  subCategoryResponse: ISupabaseSpecificationCategory
): ISpecificationCategoryResponse[] => {
  return categories.map((category) => {
    // If this category is the parent of the new subcategory
    if (category.id === subCategoryResponse.parentId) {
      // Create new category object
      const newCategory: ISpecificationCategoryResponse = {
        id: subCategoryResponse.id,
        type: "category",
        name: subCategoryResponse.name,
        position: "",
        children: null,
      };

      // Update children array with new subcategory
      const updatedChildren = addSubCategoryToChildren(category, newCategory);

      return {
        ...category,
        children: updatedChildren,
      };
    }

    // If this category is not the parent, search in subcategories
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      const updatedSubCategories = updateCategoryRecursively(
        subCategories,
        subCategoryResponse
      );

      // Rebuild children array preserving original order
      const currentChildren = category.children || [];
      const updatedChildren = currentChildren.map((child) => {
        if (child.type === "category") {
          const updatedSub = updatedSubCategories.find((sub) => sub.id === child.id);
          return updatedSub || child;
        }
        return child;
      });

      return {
        ...category,
        children: updatedChildren,
      };
    }

    // No changes made, return category as is
    return category;
  });
};

export const updateItemRecursively = (
  categories: ISpecificationCategoryResponse[],
  itemResponse: ISupabaseSpecificationItem
): ISpecificationCategoryResponse[] => {
  // Check if categoryId exists and is not empty
  if (!itemResponse.categoryId || itemResponse.categoryId === "") {
    console.warn(
      `⚠️ Item ${itemResponse.id} has no categoryId in updateItemRecursively. Skipping item.`
    );
    return categories;
  }

  // If item is deleted, remove it from categories
  if (itemResponse.deletedAt !== null && itemResponse.deletedAt !== undefined) {
    return categories.map((category) => {
      const subCategories = getSubCategories(category);
      const updatedSubCategories =
        subCategories.length > 0
          ? updateItemRecursively(subCategories, itemResponse)
          : [];

      // Rebuild children array preserving original order
      const currentChildren = category.children || [];
      let updatedChildren = currentChildren.filter(
        (child) => !(child.type === "item" && child.id === itemResponse.id)
      );

      // Update subcategories in place
      if (updatedSubCategories.length > 0) {
        updatedChildren = updatedChildren.map((child) => {
          if (child.type === "category") {
            const updatedSub = updatedSubCategories.find((sub) => sub.id === child.id);
            return updatedSub || child;
          }
          return child;
        });
      }

      return {
        ...category,
        children: updatedChildren,
      };
    });
  }

  // Create new item object
  const newItem: ISpecificationItemResponse = {
    id: itemResponse.id,
    type: "item",
    description: itemResponse.description || "",
    score: itemResponse.score,
    position: "",
  };

  return categories.map((category) => {
    // If item belongs to this category
    if (category.id === itemResponse.categoryId) {
      // Update children array with new/updated item
      const updatedChildren = addItemToChildren(category, newItem);

      return {
        ...category,
        children: updatedChildren,
      };
    }

    // If this is not the right category, search in subcategories
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      const updatedSubCategories = updateItemRecursively(
        subCategories,
        itemResponse
      );

      // Rebuild children array preserving original order
      const currentChildren = category.children || [];
      const updatedChildren = currentChildren.map((child) => {
        if (child.type === "category") {
          const updatedSub = updatedSubCategories.find((sub) => sub.id === child.id);
          return updatedSub || child;
        }
        return child;
      });

      return {
        ...category,
        children: updatedChildren,
      };
    }

    // No changes made, return category as is
    return category;
  });
};

export const getAllItems = (
  categories: ISpecificationCategoryResponse[]
): ISpecificationItemResponseExtended[] => {
  if (!categories) return [];

  const result = categories.reduce((acc, category) => {
    // Get items from children array
    const items = getItems(category);

    const itemsWithCategory = items.map((item) => ({
      ...item,
      category: { id: category.id },
      orderNo: typeof item.orderNo === 'string' ? parseInt(item.orderNo, 10) || null : item.orderNo,
    } as unknown as ISpecificationItemResponseExtended));

    // Get subcategories and recursively get their items
    const subCategories = getSubCategories(category);
    const subCategoryItems =
      subCategories.length > 0 ? getAllItems(subCategories) : [];

    return [...acc, ...subCategoryItems, ...itemsWithCategory];
  }, [] as ISpecificationItemResponseExtended[]);

  return result;
};

export const getAllCategoriesAndSubCategories = (
  categories: ISpecificationCategoryResponse[]
): {
  mainCategories: ISpecificationCategoryResponse[];
  subCategories: ISpecificationCategoryResponse[];
} => {
  let allSubCategories: ISpecificationCategoryResponse[] = [];

  // Recursive function to collect subcategories
  const collectSubCategories = (category: ISpecificationCategoryResponse) => {
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      subCategories.forEach((subCategory) => {
        allSubCategories.push(subCategory);
        collectSubCategories(subCategory);
      });
    }
  };

  // Main categories are root level categories (no parent in tree structure)
  // Since we don't have parent property, we consider all top-level categories as main
  const mainCategories = categories;

  // Collect subcategories from each main category
  mainCategories.forEach((category) => {
    collectSubCategories(category);
  });

  return {
    mainCategories,
    subCategories: allSubCategories,
  };
};

export const supabaseResponseToItem = (
  item: ISupabaseSpecificationItem
): ISpecificationItemResponseExtended => {
  return {
    id: item.id,
    type: "item",
    description: item.description,
    score: item.score,
    position: "",
    orderNo: item.orderNo,
    isNew: item.isNew,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    deletedAt: item.deletedAt,
    isActive: item.isActive,
    category: {
      id: item.categoryId,
    },
  };
};

export const convertAllItemsToSupabaseFormat = (
  items: ISpecificationItemResponseExtended[],
  specificationId: string
): ISupabaseSpecificationItem[] => {
  return items.map((item) => {
    // Convert orderNo to number: parse string, use number directly, or default to 0
    const orderNo = typeof item.orderNo === 'string'
      ? parseInt(item.orderNo, 10) || 0
      : (item.orderNo ?? 0);

    return {
      categoryId: item.category?.id || "",
      createdAt: item.createdAt || "",
      deletedAt: item.deletedAt || null,
      description: item.description,
      score: item.score ?? 0,
      id: item.id,
      isActive: true,
      orderNo,
      specificationId: specificationId,
      updatedAt: item.updatedAt || "",
      isNew: item.isNew ?? false,
    };
  });
};

export const convertAllCategoriesToSupabaseFormat = (
  categories: ISpecificationCategoryResponse[],
  specificationId: string
): ISupabaseSpecificationCategory[] => {
  return categories.map((category) => ({
    id: category.id,
    name: category.name,
    createdAt: (category as any).createdAt || "",
    updatedAt: (category as any).updatedAt || "",
    deletedAt: (category as any).deletedAt || null,
    isActive: (category as any).isActive ?? true,
    parentId: (category as any).parent || null,
    specificationId: specificationId,
  }));
};

export const addCategoryToCategories = (
  categories: ISpecificationCategoryResponse[],
  category: ISpecificationCategoryResponse,
  parentId: string | null
): ISpecificationCategoryResponse[] => {
  // Create new category
  const newCat: ISpecificationCategoryResponse = {
    ...category,
  };

  // Recursively find category with parentId and add new category to its children
  const updateCategories = (
    cats: ISpecificationCategoryResponse[]
  ): ISpecificationCategoryResponse[] => {
    return cats.map((cat) => {
      if (cat.id === parentId) {
        // Found parent category, add new category to children
        const subCategories = getSubCategories(cat);
        const items = getItems(cat);
        return {
          ...cat,
          children: [...subCategories, newCat, ...items],
        };
      }

      // Continue searching in subcategories
      const subCategories = getSubCategories(cat);
      if (subCategories.length > 0) {
        const updatedSubCategories = updateCategories(subCategories);
        const items = getItems(cat);
        return {
          ...cat,
          children: [...updatedSubCategories, ...items],
        };
      }

      return cat;
    });
  };

  return parentId != null && parentId != ""
    ? updateCategories(categories)
    : [...categories, newCat];
};

export const findCategoryByIdItems = (
  categories: ISpecificationCategoryResponse[],
  id: string,
  parentId: string
): ISpecificationItemResponse[] => {
  let items: ISpecificationItemResponse[] = [];

  // Check each category
  for (const category of categories) {
    // If target category found (parentId check removed as parent property doesn't exist)
    if (category.id === id) {
      items = getItems(category);
      break;
    }

    // Search in subcategories
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      const subCategoryItems = findCategoryByIdItems(
        subCategories,
        id,
        parentId
      );
      if (subCategoryItems.length > 0) {
        items = subCategoryItems;
        break;
      }
    }
  }

  return items;
};

export const findCategoryById = (
  categories: ISpecificationCategoryResponse[],
  id: string
): ISpecificationCategoryResponse | undefined => {
  for (const category of categories) {
    if (category.id === id) {
      return category;
    }
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      const subCategory = findCategoryById(subCategories, id);
      if (subCategory) {
        return subCategory;
      }
    }
  }
};

export const removeCategoryRecursively = (
  categories: ISpecificationCategoryResponse[],
  id: string
): ISpecificationCategoryResponse[] => {
  if (!categories || categories.length === 0) {
    return [];
  }

  // Filter out the category with matching id
  const filtered = categories.filter((category) => category.id !== id);

  const result = filtered.map((category) => {
    // Recursively filter subcategories
    const subCategories = getSubCategories(category);
    const updatedSubCategories =
      subCategories.length > 0
        ? removeCategoryRecursively(subCategories, id)
        : [];

    // Rebuild children array
    const items = getItems(category);
    return {
      ...category,
      children: [...updatedSubCategories, ...items],
    };
  });

  return result;
};

export const updateCategoryNameRecursively = (
  categories: ISpecificationCategoryResponse[],
  categoryId: string,
  newName: string
): ISpecificationCategoryResponse[] => {
  if (!categories || categories.length === 0) {
    return [];
  }

  return categories.map((category) => {
    // If this is the category to update
    if (category.id === categoryId) {
      const subCategories = getSubCategories(category);
      const items = getItems(category);
      return {
        ...category,
        name: newName,
        children: [...subCategories, ...items],
      };
    }

    // If not, search in subcategories
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      const updatedSubCategories = updateCategoryNameRecursively(
        subCategories,
        categoryId,
        newName
      );
      const items = getItems(category);
      return {
        ...category,
        children: [...updatedSubCategories, ...items],
      };
    }

    // No changes made, return category as is
    return category;
  });
};

export const removeItemRecursively = (
  categories: ISpecificationCategoryResponse[],
  categoryId: string,
  itemId: string
): ISpecificationCategoryResponse[] => {
  if (!categories || categories.length === 0) {
    return [];
  }

  return categories.map((category) => {
    // If this is the target category, filter items
    if (category.id === categoryId) {
      const subCategories = getSubCategories(category);
      const updatedSubCategories = removeItemRecursively(
        subCategories,
        categoryId,
        itemId
      );
      const filteredItems = removeItemFromChildren(category, itemId).filter(
        (child) => child.type === "item"
      ) as ISpecificationItemResponse[];

      return {
        ...category,
        children: [...updatedSubCategories, ...filteredItems],
      };
    }

    // If not target category, search in subcategories
    const subCategories = getSubCategories(category);
    if (subCategories.length > 0) {
      const updatedSubCategories = removeItemRecursively(
        subCategories,
        categoryId,
        itemId
      );
      const items = getItems(category);
      return {
        ...category,
        children: [...updatedSubCategories, ...items],
      };
    }

    // No changes, return as is
    return category;
  });
};

export const removeItemByIdFromAllCategories = (
  categories: ISpecificationCategoryResponse[],
  itemId: string
): ISpecificationCategoryResponse[] => {
  if (!categories || categories.length === 0) {
    return [];
  }

  return categories.map((category) => {
    // Remove item from this category's children
    const filteredChildren = removeItemFromChildren(category, itemId);

    // Recursively check subcategories
    const subCategories = getSubCategories(category);
    const updatedSubCategories =
      subCategories.length > 0
        ? removeItemByIdFromAllCategories(subCategories, itemId)
        : [];

    // Rebuild children array
    const items = filteredChildren.filter(
      (child) => child.type === "item"
    ) as ISpecificationItemResponse[];

    return {
      ...category,
      children: [...updatedSubCategories, ...items],
    };
  });
};

export const getStateCategories = () => {
  return store.getState().specification.categories;
};

/**
 * Checks if a category or its children match the search term
 * Used for conditional rendering in mapping functions
 */
export const categoryMatchesSearch = (
  category: ISpecificationCategoryResponse,
  searchTerm: string
): boolean => {
  if (!searchTerm || searchTerm.trim() === "") {
    return true;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // Check if category name matches
  const categoryNameMatches = category.name
    ?.toLowerCase()
    .includes(normalizedSearchTerm);

  if (categoryNameMatches) {
    return true;
  }

  // Check if any items match
  const items = getItems(category);
  const hasMatchingItems = items.some((item) =>
    item.description?.toLowerCase().includes(normalizedSearchTerm)
  );

  if (hasMatchingItems) {
    return true;
  }

  // Check if any subcategories match (recursively)
  const subCategories = getSubCategories(category);
  const hasMatchingSubCategories = subCategories.some((subCategory) =>
    categoryMatchesSearch(subCategory, searchTerm)
  );

  return hasMatchingSubCategories;
};

/**
 * Checks if an item matches the search term
 */
export const itemMatchesSearch = (
  item: ISpecificationItemResponse,
  searchTerm: string
): boolean => {
  if (!searchTerm || searchTerm.trim() === "") {
    return true;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  return item.description?.toLowerCase().includes(normalizedSearchTerm) || false;
};
